/**
 * What a task LOOKS like — labels, tones and the few decisions the screens
 * share. Pure: no React, no fetch, and the clock is an argument, so every rule
 * here is tested as a function (apps/web/test/tasks.test.mjs).
 */
import { differenceInCalendarDays, format } from 'date-fns';
import type { Task, TaskPriority, TaskStatus, TaskView } from '@cyphex/types/admin';

/** The tones `Pill` draws; kept to the ones a status or a priority can be. */
export type Tone = 'neutral' | 'info' | 'warning' | 'danger' | 'success';

export const STATUS_LABEL: Record<TaskStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  blocked: 'Blocked',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const STATUS_TONE: Record<TaskStatus, Tone> = {
  not_started: 'neutral',
  in_progress: 'info',
  blocked: 'danger',
  completed: 'success',
  cancelled: 'neutral',
};

export const PRIORITY_LABEL: Record<TaskPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

/** `null` for the two priorities that are not worth a badge on a row. */
export const PRIORITY_TONE: Record<TaskPriority, Tone | null> = {
  low: null,
  normal: null,
  high: 'warning',
  urgent: 'danger',
};

export const PRIORITY_ORDER: readonly TaskPriority[] = ['low', 'normal', 'high', 'urgent'];

/** Still to be done. A cancelled task is as finished as a completed one. */
export const isOpen = (status: TaskStatus): boolean =>
  status !== 'completed' && status !== 'cancelled';

export const VIEW_LABEL: Record<TaskView, string> = {
  assigned: 'Assigned to me',
  created: 'Assigned by me',
  watching: "CC'd to me",
};

export const isView = (value: string | null): value is TaskView =>
  value === 'assigned' || value === 'created' || value === 'watching';

/* ---- the date ------------------------------------------------------------ */

export interface DueInfo {
  label: string;
  /** `late`: past its date. `soon`: today or tomorrow. `plain`: neither, or finished. */
  tone: 'late' | 'soon' | 'plain';
}

/**
 * The date, as a person would say it, and how urgently.
 *
 * In the reader's own time zone — this runs in their browser — and measured
 * against the INSTANT, not the day: a task due at ten this morning is late at
 * eleven, and "Due today" would be a comfort it has not earned. A finished
 * task's date is just a date.
 */
export function dueInfo(dueAt: string | null, status: TaskStatus, now: Date): DueInfo | null {
  if (dueAt === null) return null;
  const at = new Date(dueAt);
  if (Number.isNaN(at.getTime())) return null;
  const time = format(at, 'h:mm a');
  if (!isOpen(status)) return { label: `Due ${format(at, 'd MMM')}`, tone: 'plain' };

  const days = differenceInCalendarDays(at, now);
  if (at.getTime() < now.getTime()) {
    const since = days === 0 ? `today, ${time}` : days === -1 ? 'yesterday' : format(at, 'd MMM');
    return { label: `Overdue · ${since}`, tone: 'late' };
  }
  if (days === 0) return { label: `Due today, ${time}`, tone: 'soon' };
  if (days === 1) return { label: `Due tomorrow, ${time}`, tone: 'soon' };
  if (days < 7) return { label: `Due ${format(at, 'EEE')}, ${time}`, tone: 'plain' };
  return {
    label: `Due ${format(at, at.getFullYear() === now.getFullYear() ? 'd MMM' : 'd MMM yyyy')}`,
    tone: 'plain',
  };
}

/** The date in full, for the task's own page: "Fri 3 Oct 2026, 6:00 PM". */
export function dueInFull(dueAt: string): string {
  return format(new Date(dueAt), 'EEE d MMM yyyy, h:mm a');
}

/* ---- `<input type="datetime-local">` ------------------------------------- */

const pad = (value: number): string => String(value).padStart(2, '0');

/** An instant → the value a `datetime-local` input shows, in local time. */
export function toLocalInput(iso: string | null): string {
  if (iso === null) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const day = `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  return `${day}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** The input's value → an instant. `null` for an empty or unreadable box. */
export function fromLocalInput(value: string): string | null {
  if (value.trim() === '') return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** The end of the working day: what "due Friday" means when no hour is given. */
export const END_OF_DAY_HOUR = 18;

export type QuickDue = 'today' | 'tomorrow' | 'nextWeek';

export const QUICK_DUE: ReadonlyArray<{ key: QuickDue; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'nextWeek', label: 'Next week' },
];

/**
 * A one-press date, as the input's value. Six in the evening on that day —
 * unless "today" at six has already gone, in which case it is the end of today:
 * a date that is overdue the moment it is chosen is not what anybody meant.
 */
export function quickDue(which: QuickDue, now: Date): string {
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate(), END_OF_DAY_HOUR, 0, 0, 0);
  if (which === 'tomorrow') day.setDate(day.getDate() + 1);
  if (which === 'nextWeek') day.setDate(day.getDate() + 7);
  if (which === 'today' && day.getTime() <= now.getTime()) day.setHours(23, 59, 0, 0);
  return toLocalInput(day.toISOString());
}

/* ---- who ----------------------------------------------------------------- */

export type TaskRole = 'creator' | 'assignee' | 'watcher';

/** What the reader is on a task. The strongest one: creator, then assignee, then CC. */
export function roleOf(task: Task, viewerId: string | null): TaskRole | null {
  if (viewerId === null) return null;
  if (task.createdBy?.id === viewerId) return 'creator';
  if (task.assignees.some((person) => person.id === viewerId)) return 'assignee';
  if (task.watchers.some((person) => person.id === viewerId)) return 'watcher';
  return null;
}

/**
 * May the reader finish it from the list, without opening it? The ones doing
 * it and the one who set it. (Whoever moderates the place a task lives in can
 * too, but only the task's own page knows that — it asks the server.)
 */
export function canProgressFromList(task: Task, viewerId: string | null): boolean {
  const role = roleOf(task, viewerId);
  return role === 'creator' || role === 'assignee';
}

const namesOf = (people: Task['assignees'], viewerId: string | null): string[] =>
  people.map((person) => (person.id === viewerId ? 'you' : person.displayName));

/** "Asha", "Asha and Ravi", "Asha and 3 others". */
export function listed(names: readonly string[]): string {
  if (names.length <= 2) return names.join(' and ');
  return `${names[0] ?? ''} and ${String(names.length - 1)} others`;
}

/**
 * The line under a task's title: who it is between, from where the reader
 * stands. In "Assigned to me" the news is who set it; in "Assigned by me" it is
 * who has it; when following, both.
 */
export function peopleLine(task: Task, viewerId: string | null): string {
  const creator = task.createdBy;
  const from = creator === null ? null : creator.id === viewerId ? 'you' : creator.displayName;
  const role = roleOf(task, viewerId);

  if (task.assignees.length === 0) {
    return from === null ? 'Nobody is assigned' : `Created by ${from} · nobody assigned`;
  }
  // Set by somebody for themselves alone.
  if (creator !== null && task.assignees.every((person) => person.id === creator.id)) {
    return role === 'creator' ? 'Your own task' : `${creator.displayName}'s own task`;
  }

  const to = listed(namesOf(task.assignees, viewerId));
  if (role === 'creator') return `To ${to}`;
  if (role === 'assignee') {
    const others = task.assignees.filter((person) => person.id !== viewerId);
    const together = others.length === 0 ? '' : ` · with ${listed(namesOf(others, viewerId))}`;
    return `From ${from ?? 'somebody who has left'}${together}`;
  }
  return `${from ?? 'Somebody'} → ${to}`;
}

/** "2 of 5", or null for a task with no checklist. */
export function checklistProgress(task: Task): { done: number; total: number } | null {
  if (task.checklist.length === 0) return null;
  return { done: task.checklist.filter((item) => item.done).length, total: task.checklist.length };
}

/* ---- the lists ----------------------------------------------------------- */

export interface ListFilters {
  view: TaskView | null;
  state: 'open' | 'closed';
  q: string;
  scope: { kind: 'channel'; id: string } | null;
}

/** The query string of one list. Stable key order, so it is also a cache key. */
export function listParams(filters: ListFilters): Record<string, string | number> {
  return {
    ...(filters.scope === null
      ? { view: filters.view ?? 'assigned' }
      : { scopeKind: filters.scope.kind, scopeId: filters.scope.id }),
    state: filters.state,
    ...(filters.q.trim() === '' ? {} : { q: filters.q.trim() }),
    limit: 200,
  };
}

/** What an empty list says. It depends on which list, and on whether it was searched. */
export function emptyText(filters: ListFilters): { title: string; description: string } {
  if (filters.q.trim() !== '') {
    return { title: 'No task matches that', description: 'Try fewer words, or another list.' };
  }
  if (filters.state === 'closed') {
    return {
      title: 'Nothing finished yet',
      description: 'Completed and cancelled tasks are kept here.',
    };
  }
  if (filters.scope !== null) {
    return {
      title: 'No open tasks in this channel',
      description: 'A task made here is seen by everyone in the channel.',
    };
  }
  switch (filters.view) {
    case 'created':
      return {
        title: 'You have not assigned a task to anyone',
        description: 'Tasks you give to other people are tracked here until they are done.',
      };
    case 'watching':
      return {
        title: 'You are not in CC on anything',
        description: 'Tasks other people copy you on appear here, so you can follow them.',
      };
    default:
      return { title: 'Nothing to do', description: 'Tasks assigned to you will appear here.' };
  }
}
